import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Spinner,
    Button,
    Alert,
    Nav,
    NavItem,
    NavLink,
    Card,
    CardBody,
    CardHeader,
    Badge,
} from "reactstrap";
import moment from "moment";
import api from "../services/api";
import UserContext from "../contexts/UserContext";
import AuthService from "../services/authService";
import Header from "../components/public/Header";
import Footer from "../components/public/Footer";

const UpcomingSchedule = () => {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [organization, setOrganization] = useState(null);
    const [leagueName, setLeagueName] = useState("League");
    const [showLeaderboard, setShowLeaderboard] = useState(false);

    const getEventLeaderboardFlag = (event) => {
        return event?.show_leaderboard
            ?? event?.league?.show_leaderboard
            ?? event?.league_show_leaderboard
            ?? false;
    };

    useEffect(() => {
        api.get("/get-org-details/")
            .then((response) => {
                setOrganization(response.data);
            });
    }, []);

    useEffect(() => {
        document.title = "Upcoming Schedule";

        // Check authentication from both UserContext and localStorage
        const currentUser = user || AuthService.getCurrentUser();
        if (!currentUser?.isCustomer) {
            const upcomingPath = leagueId ? `/league/${leagueId}/upcoming` : "/league/upcoming";
            navigate("/customer-login", { state: { from: upcomingPath } });
            return;
        }

        const fetchData = async () => {
            try {
                const [eventResponse] = await Promise.all([
                    api.get('/customer/league-events/', {
                        params: leagueId ? { league_id: leagueId } : {},
                    }),
                ]);
                const allEvents = Array.isArray(eventResponse.data?.events) ? eventResponse.data.events : [];
                const filteredEvents = leagueId
                    ? allEvents.filter((event) => String(event.league_guid) === String(leagueId))
                    : allEvents;

                setEvents(filteredEvents);

                if (filteredEvents[0]?.league_name) {
                    setLeagueName(filteredEvents[0].league_name);
                }

                if (leagueId) {
                    setShowLeaderboard(Boolean(getEventLeaderboardFlag(filteredEvents[0])));
                }
            } catch (err) {
                console.error("Error loading upcoming schedule:", err);
                setError("Failed to load your upcoming schedule. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, navigate, leagueId]);

    if (loading) {
        return (
            <>
                <Header organization={organization} showBanner={false} />
                <Container className="text-center mt-5">
                    <Spinner color="primary" />
                    <p className="mt-3">Loading your team schedule...</p>
                </Container>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header organization={organization} showBanner={false} />
                <Container className="text-center mt-5">
                    <Alert color="danger">{error}</Alert>
                    <Button outline color="dark" onClick={() => navigate("/")}>
                        Go Home
                    </Button>
                </Container>
                <Footer />
            </>
        );
    }

    // --- Group events by league ---
    const groupedByLeague = (Array.isArray(events) ? events : []).reduce((acc, event) => {
        const leagueTitle = event.league_name || "Unknown League";
        const leagueGUID = event.league_guid;
        if (!acc[leagueTitle]) {
            acc[leagueTitle] = {
                leagueName: leagueTitle,
                leagueGUID: leagueGUID,
                events: []
            };
        }
        acc[leagueTitle].events.push(event);
        return acc;
    }, {});

    const currentUser = user || AuthService.getCurrentUser();

    return (
        <>
            <Header organization={organization} showBanner={false} />
            <Container className="mt-4">
                <Row className="mb-4">
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <h2 className="mb-0 text-start">{leagueId ? leagueName : "Upcoming Schedule"}</h2>
                    </Col>
                    <Col xs={12} md={6} className="text-md-end">
                        <div className="d-flex flex-column align-items-md-end">
                            <div className="text-muted mb-2">
                                Logged in as: <strong>{currentUser?.email}</strong>
                            </div>
                            <Button
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => {
                                    AuthService.logout();
                                    navigate("/customer-login");
                                }}
                            >
                                Logout
                            </Button>
                        </div>
                    </Col>
                </Row>

                {leagueId && (
                    <Nav tabs className="mb-4 league-route-tabs">
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/league/${leagueId}/schedule`)}
                            >
                                Full Schedule
                            </NavLink>
                        </NavItem>
                        {showLeaderboard && (
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate(`/league/${leagueId}/scores`)}
                                >
                                    Leaderboard
                                </NavLink>
                            </NavItem>
                        )}
                        <NavItem>
                            <NavLink className="active" style={{ cursor: "pointer" }}>
                                Upcoming
                            </NavLink>
                        </NavItem>
                    </Nav>
                )}

                {!Array.isArray(events) || events.length === 0 ? (
                    <Row className="mb-4 text-center">
                        <Col>
                            <Alert color="secondary" fade={false}>
                                You don't have any scheduled league events yet.
                            </Alert>
                        </Col>
                    </Row>
                ) : (
                    <Row className="justify-content-center">
                        {Object.entries(groupedByLeague).map(([leagueTitle, { leagueGUID, leagueName, events: leagueEvents }]) => {
                            // Group by team
                            const groupedByTeam = leagueEvents.reduce((acc, event) => {
                                const teamName = event.team_name || "Unnamed Team";
                                if (!acc[teamName]) {
                                    acc[teamName] = [];
                                }
                                acc[teamName].push(event);
                                return acc;
                            }, {});

                            return Object.entries(groupedByTeam).map(([teamName, teamEvents]) => {
                                // Sort events by week and date
                                const sortedEvents = teamEvents.sort((a, b) => {
                                    if (a.week_number !== b.week_number) {
                                        return parseInt(a.week_number) - parseInt(b.week_number);
                                    }
                                    return moment(a.event_start).diff(moment(b.event_start));
                                });

                                return (
                                    <React.Fragment key={`${leagueTitle}-${teamName}`}>
                                        <Col xs={12} className="mb-3">
                                            <Card className="shadow-sm border-0 bg-light">
                                                <CardBody className="py-2 px-3">
                                                    <div className="fw-semibold">{teamName}</div>
                                                    {!leagueId && (
                                                        <div className="text-muted small">{leagueName}</div>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {sortedEvents.map((item) => (
                                            <Col key={item.id} xs={12} lg={4} className="mb-4">
                                                <Card className="shadow-sm h-100">
                                                    <CardHeader className="d-flex justify-content-between align-items-center gap-2">
                                                        <Badge color="dark" pill>
                                                            Week {item.week_number || "N/A"}
                                                        </Badge>
                                                        <span className="small text-muted">
                                                            {moment(item.event_start).format("ddd MMM DD [at] h:mm A")}
                                                        </span>
                                                    </CardHeader>
                                                    <CardBody className="d-flex flex-column">
                                                        {item.week_notes && (
                                                            <div className="text-muted small">{item.week_notes}</div>
                                                        )}
                                                        <div className="mt-auto pt-3 d-flex justify-content-end">
                                                            <Button
                                                                size="sm"
                                                                style={{
                                                                    backgroundColor: "#3c6e51",
                                                                    color: "#ffffff",
                                                                    fontWeight: "normal",
                                                                    border: "none",
                                                                }}
                                                                onClick={() => window.open(`/switch/${item.guid}`, "_blank", "noopener,noreferrer")}
                                                            >
                                                                Reschedule
                                                            </Button>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </React.Fragment>
                                );
                            });
                        })}
                    </Row>
                )}
            </Container>
            <Footer />
        </>
    );
};

export default UpcomingSchedule;
